import { useEffect, useState } from "react"

import { ApiError } from "@/api/client"
import {
  useCancelMyAccountRequest,
  useCancelMyTopUpRequest,
  useMyAccountRequests,
  useMyTopUpRequests,
  type AccountRequest,
  type TopUpRequest,
} from "@/api/hooks/requests"
import { useAuth } from "@/auth/AuthProvider"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useMarkAccountCompletionsSeen } from "@/lib/seenAccountCompletions"

function formatDate(value: string | null): string {
  if (!value) return "—"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

function formatAmount(amount: string | number, currency: string): string {
  const n = typeof amount === "number" ? amount : Number(amount)
  if (!Number.isFinite(n)) return `${currency} ${String(amount)}`
  const text = Number.isInteger(n) ? String(n) : n.toFixed(2)
  return `${currency} ${text}`
}

function statusLabel(status: string): string {
  if (status === "pending") return "New"
  if (status === "in_progress") return "In Progress"
  if (status === "completed") return "Complete"
  if (status === "approved") return "Approved"
  if (status === "cancelled") return "Cancelled"
  return status.replace(/_/g, " ")
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === "approved" || status === "completed"
      ? "bg-[#1e4d3e] text-[#4ade80]"
      : status === "cancelled"
        ? "bg-[#4a2a2a] text-[#f87171]"
        : status === "in_progress"
          ? "bg-[#3a3a1e] text-[#fbbf24]"
          : "bg-[#2d3a4a] text-[#93c5fd]"

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${tone}`}
    >
      {statusLabel(status)}
    </span>
  )
}

type RequestSubTab = "top-up" | "accounts"

function TopUpHistoryTable({
  rows,
  busyId,
  onCancel,
}: {
  rows: TopUpRequest[]
  busyId: number | null
  onCancel: (id: number) => void
}) {
  if (rows.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-[#888]">
        No top-up requests yet. Use TopUp on the Accounts tab to submit one.
      </p>
    )
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-[#555555]">
      <table className="w-full min-w-[720px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-[#555555] bg-[#444444] text-left text-xs font-semibold uppercase tracking-wide text-[#b0b0b0]">
            <th className="px-4 py-3">Account</th>
            <th className="px-4 py-3">Amount</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Submitted</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-[#3a3a3a]">
          {rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-[#555555] hover:bg-[#4a4a4a]"
            >
              <td className="px-4 py-3">
                <div className="font-semibold text-[#e0e0e0]">
                  {row.advertiser_name}
                </div>
                <div className="mt-1 font-mono text-xs text-[#b0b0b0]">
                  ID: {row.advertiser_id}
                </div>
              </td>
              <td className="px-4 py-3 text-[#e0e0e0]">
                {formatAmount(row.amount, row.currency)}
              </td>
              <td className="px-4 py-3">
                <StatusPill status={row.status} />
              </td>
              <td className="px-4 py-3 text-[#b0b0b0]">
                {formatDate(row.created_at)}
              </td>
              <td className="px-4 py-3 text-right">
                {row.status === "pending" ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busyId === row.id}
                    onClick={() => onCancel(row.id)}
                    className="border-[#663333] bg-transparent text-[#f87171] hover:bg-[#4a2a2a] hover:text-[#fca5a5]"
                  >
                    {busyId === row.id ? "Cancelling..." : "Cancel"}
                  </Button>
                ) : (
                  <span className="text-xs text-[#666]">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AccountHistoryTable({
  rows,
  busyId,
  onCancel,
}: {
  rows: AccountRequest[]
  busyId: number | null
  onCancel: (id: number) => void
}) {
  if (rows.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-[#888]">
        No account requests yet. Use Request new ad account to submit one.
      </p>
    )
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-[#555555]">
      <table className="w-full min-w-[800px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-[#555555] bg-[#444444] text-left text-xs font-semibold uppercase tracking-wide text-[#b0b0b0]">
            <th className="px-4 py-3">Network</th>
            <th className="px-4 py-3">Agency</th>
            <th className="px-4 py-3">TopUp</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Details</th>
            <th className="px-4 py-3">Submitted</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-[#3a3a3a]">
          {rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-[#555555] hover:bg-[#4a4a4a]"
            >
              <td className="px-4 py-3 text-[#e0e0e0]">{row.network_name}</td>
              <td className="px-4 py-3 text-[#e0e0e0]">{row.agency}</td>
              <td className="px-4 py-3 text-[#e0e0e0]">
                {formatAmount(row.top_up_amount, "USD")}
              </td>
              <td className="px-4 py-3">
                <StatusPill status={row.status} />
              </td>
              <td className="max-w-[280px] px-4 py-3 text-[#b0b0b0]">
                {row.fulfillment_note ? (
                  <span className="whitespace-pre-wrap text-[#e0e0e0]">
                    {row.fulfillment_note}
                  </span>
                ) : (
                  "—"
                )}
              </td>
              <td className="px-4 py-3 text-[#b0b0b0]">
                {formatDate(row.created_at)}
              </td>
              <td className="px-4 py-3 text-right">
                {row.status === "pending" ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busyId === row.id}
                    onClick={() => onCancel(row.id)}
                    className="border-[#663333] bg-transparent text-[#f87171] hover:bg-[#4a2a2a] hover:text-[#fca5a5]"
                  >
                    {busyId === row.id ? "Cancelling..." : "Cancel"}
                  </Button>
                ) : (
                  <span className="text-xs text-[#666]">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

type MyRequestsPanelProps = {
  /** Controlled sub-tab so the parent page can keep chrome consistent. */
  subTab: RequestSubTab
  onSubTabChange: (tab: RequestSubTab) => void
}

export function MyRequestsPanel({
  subTab,
  onSubTabChange,
}: MyRequestsPanelProps) {
  const { user } = useAuth()
  const topUpsQuery = useMyTopUpRequests()
  const accountsQuery = useMyAccountRequests()
  const markCompletionsSeen = useMarkAccountCompletionsSeen(user?.id)
  const cancelTopUp = useCancelMyTopUpRequest()
  const cancelAccount = useCancelMyAccountRequest()
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  // Opening Account Requests clears the Accounts sidebar badge for completed rows.
  useEffect(() => {
    if (subTab !== "accounts") return
    const completedIds = (accountsQuery.data?.data ?? [])
      .filter((r) => r.status === "completed")
      .map((r) => r.id)
    markCompletionsSeen(completedIds)
  }, [subTab, accountsQuery.data, markCompletionsSeen])

  const cancelTopUpRow = async (id: number) => {
    setError(null)
    setBusyId(id)
    try {
      await cancelTopUp.mutateAsync(id)
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to cancel request",
      )
    } finally {
      setBusyId(null)
    }
  }

  const cancelAccountRow = async (id: number) => {
    setError(null)
    setBusyId(id)
    try {
      await cancelAccount.mutateAsync(id)
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to cancel request",
      )
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onSubTabChange("top-up")}
          className={[
            "rounded-md px-4 py-2 text-sm font-semibold transition-colors",
            subTab === "top-up"
              ? "bg-[#667eea]/25 text-[#8b9aff]"
              : "bg-[#2d2d2d] text-[#b0b0b0] hover:text-white",
          ].join(" ")}
        >
          TopUp Requests
        </button>
        <button
          type="button"
          onClick={() => onSubTabChange("accounts")}
          className={[
            "rounded-md px-4 py-2 text-sm font-semibold transition-colors",
            subTab === "accounts"
              ? "bg-[#667eea]/25 text-[#8b9aff]"
              : "bg-[#2d2d2d] text-[#b0b0b0] hover:text-white",
          ].join(" ")}
        >
          Account Requests
        </button>
      </div>

      {error ? (
        <Alert
          variant="destructive"
          className="border-[#663333] bg-[#4a2a2a] text-[#f87171]"
        >
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {subTab === "top-up" ? (
        topUpsQuery.isLoading ? (
          <p className="py-6 text-center text-sm text-[#b0b0b0]">Loading...</p>
        ) : topUpsQuery.isError ? (
          <p className="py-6 text-center text-sm text-[#f87171]">
            Failed to load top-up requests
          </p>
        ) : (
          <TopUpHistoryTable
            rows={topUpsQuery.data?.data ?? []}
            busyId={busyId}
            onCancel={(id) => void cancelTopUpRow(id)}
          />
        )
      ) : accountsQuery.isLoading ? (
        <p className="py-6 text-center text-sm text-[#b0b0b0]">Loading...</p>
      ) : accountsQuery.isError ? (
        <p className="py-6 text-center text-sm text-[#f87171]">
          Failed to load account requests
        </p>
      ) : (
        <AccountHistoryTable
          rows={accountsQuery.data?.data ?? []}
          busyId={busyId}
          onCancel={(id) => void cancelAccountRow(id)}
        />
      )}
    </div>
  )
}
